<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';

// Demo countdown banner + one-click signup modal. Reads the expiry
// timestamp the login bridge wrote to sessionStorage, ticks every
// second, and when the timer hits zero (or the visitor clicks Sign up)
// opens a full screen modal that converts the demo into a real free
// account. The visitor already gave us their email at the start, so
// the only thing left to confirm is the 18+ attestation.
const EXPIRY_KEY = 'demo_expires_at';
const MODE_KEY = 'demo_mode';
const TOKEN_KEY = 'demo_token';

const remainingMs = ref(0);
const active = ref(false);
const showModal = ref(false);
const submitting = ref(false);
const errorMessage = ref('');
const termsAccepted = ref(true);
let intervalId = null;

const isDemo = () => {
  try {
    return sessionStorage.getItem(MODE_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const readExpiry = () => {
  try {
    const raw = sessionStorage.getItem(EXPIRY_KEY);
    if (!raw) return null;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch (e) {
    return null;
  }
};

const readToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || '';
  } catch (e) {
    return '';
  }
};

const minutes = computed(() =>
  Math.max(0, Math.floor(remainingMs.value / 60000))
);
const seconds = computed(() =>
  Math.max(0, Math.floor((remainingMs.value % 60000) / 1000))
);
const isLowTime = computed(() => remainingMs.value < 120000);

const formatted = computed(() => {
  const s = String(seconds.value).padStart(2, '0');
  return `${minutes.value}:${s}`;
});

const clearDemoSession = () => {
  try {
    sessionStorage.removeItem(MODE_KEY);
    sessionStorage.removeItem(EXPIRY_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    // ignore
  }
};

const openSignupModal = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  showModal.value = true;
};

const tick = () => {
  const expiry = readExpiry();
  if (!expiry) {
    remainingMs.value = 0;
    active.value = false;
    return;
  }
  const left = expiry - Date.now();
  if (left <= 0) {
    remainingMs.value = 0;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    openSignupModal();
    return;
  }
  remainingMs.value = left;
};

const handleConvert = async () => {
  if (submitting.value) return;
  if (!termsAccepted.value) {
    errorMessage.value = 'Please confirm you are 18 or older and accept the terms.';
    return;
  }
  submitting.value = true;
  errorMessage.value = '';

  const token = readToken();
  if (!token) {
    errorMessage.value = 'We could not find your demo session. Please refresh and try again.';
    submitting.value = false;
    return;
  }

  try {
    const response = await fetch(
      `/api/v1/demo_sessions/${token}/convert`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ terms_accepted: true }),
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      errorMessage.value =
        data.error || 'Something went wrong. Please try again.';
      submitting.value = false;
      return;
    }

    // Persist auth into the cookie the dashboard reads.
    document.cookie =
      'cw_d_session_info=' +
      encodeURIComponent(JSON.stringify(data.auth)) +
      '; path=/; max-age=' +
      30 * 24 * 60 * 60 +
      '; SameSite=Lax';

    clearDemoSession();
    window.location.href = data.redirect_url;
  } catch (e) {
    errorMessage.value = 'Could not reach the server. Please try again.';
    submitting.value = false;
  }
};

onMounted(() => {
  if (!isDemo()) return;
  active.value = true;
  tick();
  intervalId = setInterval(tick, 1000);
});

onBeforeUnmount(() => {
  if (intervalId) clearInterval(intervalId);
});
</script>

<template>
  <div
    v-if="active && !showModal"
    class="flex items-center justify-between gap-3 px-4 py-2 border-b"
    :class="
      isLowTime
        ? 'bg-n-ruby-3 border-n-ruby-6 text-n-ruby-11'
        : 'bg-n-amber-3 border-n-amber-6 text-n-amber-11'
    "
  >
    <div class="flex items-center gap-2 text-sm font-medium">
      <span>
        Demo workspace.
        <span class="font-mono">{{ formatted }}</span>
        remaining.
      </span>
    </div>
    <button
      type="button"
      class="text-xs font-semibold underline hover:no-underline cursor-pointer"
      @click="openSignupModal"
    >
      Sign up free to keep this
    </button>
  </div>

  <!-- Signup modal -->
  <div
    v-if="showModal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black-1 p-4"
    style="background-color: rgba(0, 0, 0, 0.6)"
  >
    <div
      class="w-full max-w-md rounded-2xl bg-n-solid-2 p-8 shadow-xl"
      style="background-color: #ffffff"
    >
      <h2 class="text-xl font-semibold mb-2 text-n-slate-12">
        Your demo has ended
      </h2>
      <p class="text-sm mb-6 text-n-slate-11">
        We saved your email when you started. One click and this becomes a
        real free account. You keep a clean workspace of your own, ready
        for your customers.
      </p>

      <label class="flex items-start gap-3 mb-6 cursor-pointer select-none">
        <input
          v-model="termsAccepted"
          type="checkbox"
          class="mt-0.5 h-4 w-4 cursor-pointer"
        />
        <span class="text-xs text-n-slate-11 leading-5">
          I confirm I am 18 or older and I accept the Synkra terms of
          service and privacy policy.
        </span>
      </label>

      <p
        v-if="errorMessage"
        class="text-xs mb-4 text-n-ruby-11"
      >
        {{ errorMessage }}
      </p>

      <div class="flex flex-col gap-3">
        <button
          type="button"
          class="w-full rounded-lg px-4 py-3 text-sm font-semibold text-white cursor-pointer disabled:opacity-50"
          style="background-color: #337418"
          :disabled="submitting"
          @click="handleConvert"
        >
          {{ submitting ? 'Creating your account…' : 'Create my free account' }}
        </button>
        <a
          href="https://synkra.co.za/demo"
          class="text-center text-xs text-n-slate-11 underline"
        >
          Not ready? Start another demo
        </a>
      </div>
    </div>
  </div>
</template>
